<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from 'vue';
import type { StageSummary } from '../utils/stages';
import { computeSegments } from '../utils/stages';
import { parseDate } from '../utils/date';

const props = defineProps<{
  stages: StageSummary[];
  buildStatus?: string;
}>();

const popoverStyle = ref<Record<string, string>>({});

function onNameHover(event: MouseEvent) {
  const el = event.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  popoverStyle.value = {
    left: `${rect.left}px`,
    top: `${rect.top - 4}px`,
    transform: 'translateY(-100%)',
  };
}

// Live timer for running stages
const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

const terminalStatuses = ['cancelled', 'failed', 'success'];

const runningStage = computed(() => props.stages.find(s => s.status === 'running'));

const isActive = computed(() => {
  if (props.buildStatus && terminalStatuses.includes(props.buildStatus)) return false;
  return !!runningStage.value;
});

function startTimer() {
  if (timer) return;
  timer = setInterval(() => { now.value = Date.now(); }, 1000);
}

function stopTimer() {
  if (timer) { clearInterval(timer); timer = null; }
}

watch(isActive, (active) => {
  if (active) startTimer();
  else stopTimer();
}, { immediate: true });

onUnmounted(() => stopTimer());

const runningElapsedMs = computed(() => {
  const currentTime = now.value;
  const stage = runningStage.value;
  if (!stage) return undefined;
  // Use startedAt from the backend if available, otherwise fall back to now
  const startMs = stage.startedAt
    ? parseDate(stage.startedAt)?.getTime() ?? currentTime
    : currentTime;
  const elapsed = currentTime - startMs;
  return elapsed > 0 ? elapsed : 0;
});

const segments = computed(() => computeSegments(props.stages, runningElapsedMs.value));
</script>

<template>
  <div class="stage-bar" v-if="segments.length > 0">
    <div class="stage-columns">
      <div
        v-for="(segment, i) in segments"
        :key="i"
        class="stage-column"
        :class="`column-${segment.status}`"
        :style="{ width: segment.widthPercent + '%' }"
      >
        <div class="stage-label">
          <span class="stage-name-wrapper" @mouseenter="onNameHover">
            <span class="stage-name">{{ segment.name }}</span>
            <span class="stage-popover" :style="popoverStyle">{{ segment.name }}</span>
          </span>
          <span class="stage-duration">{{ segment.label }}</span>
        </div>
        <div class="stage-track">
          <div
            class="stage-fill"
            :class="{ 'fill-running': segment.status === 'running' && isActive }"
            :style="{ background: segment.color }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stage-bar {
  margin-top: 10px;
}

.stage-columns {
  display: flex;
  gap: 2px;
}

.stage-column {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stage-label {
  display: flex;
  gap: 4px;
  align-items: baseline;
  margin-bottom: 3px;
  white-space: nowrap;
  min-width: 0;
}

.stage-name-wrapper {
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.stage-name {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-text-primary);
  cursor: default;
}

.stage-popover {
  visibility: hidden;
  opacity: 0;
  position: fixed;
  padding: 4px 8px;
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--color-text-primary);
  background: var(--color-card-bg);
  border: 1px solid var(--color-border-primary);
  border-radius: 4px;
  box-shadow: 0 2px 6px var(--color-card-shadow);
  white-space: nowrap;
  z-index: 100;
  pointer-events: none;
  transition: opacity 0.15s;
}

.stage-name-wrapper:hover .stage-popover {
  visibility: visible;
  opacity: 1;
}

.stage-duration {
  font-size: 0.7rem;
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.stage-track {
  height: 8px;
  border-radius: 4px;
  background: var(--color-border-primary);
  overflow: hidden;
}

.stage-fill {
  height: 100%;
  border-radius: 4px;
}

.fill-running {
  animation: running-pulse 1.5s ease-in-out infinite;
}

@keyframes running-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* On very narrow screens, hide labels and keep just the bars */
@media (max-width: 480px) {
  .stage-label {
    display: none;
  }
}
</style>
